import { useMemo, useCallback } from 'react';

import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import useAxios, { RefetchOptions } from 'axios-hooks';
import { useSnackbar } from 'notistack';

import strings, { url } from 'constants/index';
import { EntityTypes, useEntityType } from 'contexts/EntityTypesContext';
import { buildRequestParams } from 'utils/pagination.utils';

interface CurationPropertiesArgs {
  entityTypeName: EntityTypes;
  entityId: number | string;
}

interface CurationPropertiesReturn {
  curationProperties: Array<EntityCurationProperty>;
  isLoading: boolean;
  handleSubmitProperties: ({
    curationProperties,
    showMessage,
  }: Omit<CurationProperties, 'entityId'> & { showMessage?: boolean }) => void;
  refetchProperties: (
    config?: AxiosRequestConfig,
    options?: RefetchOptions,
  ) => AxiosPromise<CurationProperties>;
}

export const useCurationProperties = ({
  entityTypeName,
  entityId,
}: CurationPropertiesArgs): CurationPropertiesReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const entityType = useEntityType(entityTypeName);

  const [{ data: curationPropertyTypesData, loading: isLoadingCurationPropertyTypes }] = useAxios<
    PaginationData<EntityCurationProperty>
  >(
    {
      url: url.curation_property_types,
      params: buildRequestParams({
        filters: [{ columnId: 'entityTypeId', value: entityType?.id }],
      }),
    },
    { manual: !entityType?.id },
  );

  const [
    { data: curationPropertiesData, loading: isLoadingCurationProperties },
    refetchProperties,
  ] = useAxios<CurationProperties>(`/${entityTypeName}/${entityId}${url.curation_properties}`, {
    manual: !entityId,
  });

  const curationProperties = useMemo(() => {
    if (!curationPropertyTypesData?.items) {
      return [];
    }
    const propertyTypes = curationPropertyTypesData?.items.filter(
      item =>
        item.name !== 'ReasonForRecreate' &&
        item.name !== 'NoteForRecreate' &&
        item.name !== 'ReasonForDelete' &&
        item.name !== 'NoteForDelete',
    );
    if (curationPropertiesData) {
      return propertyTypes.map(item => {
        const value = curationPropertiesData.curationProperties[item.name];
        if (value) {
          return { ...item, value };
        }
        return item;
      });
    }
    return propertyTypes;
  }, [curationPropertyTypesData?.items, curationPropertiesData]);

  const handleSubmitProperties = useCallback(
    async ({ curationProperties, showMessage }) => {
      const updatedCurationProperties = Object.entries(curationProperties).reduce(
        (result, [key, value]) => {
          if (value) {
            result[key] = value;
          }
          return result;
        },
        {},
      );
      await axios.post(
        `/${entityTypeName}/${entityId}${url.curation_properties}`,
        {
          curationProperties: updatedCurationProperties,
        },
        {
          disableSuccessAlert: true,
        },
      );

      if (showMessage) {
        enqueueSnackbar(
          strings.entity_changed_successfully.replace(
            '{0}',
            `${entityId} ${strings.update.toLowerCase()}d`,
          ),
          { variant: 'success', style: { whiteSpace: 'pre' } },
        );
      }
    },
    [enqueueSnackbar, entityId, entityTypeName],
  );

  return {
    curationProperties,
    isLoading: isLoadingCurationPropertyTypes || isLoadingCurationProperties,
    //eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    handleSubmitProperties,
    refetchProperties,
  };
};
